/**
 * HOOK: usePaymentCaseContacts
 * Retourne la liste des contacts candidats au rôle de payeur pour un
 * payment_case donné, ou directement pour un crew avant création du BED.
 *
 * - Si le payment_case est lié à un crew : renvoie pilot + copilot (crew_members → contacts)
 * - Sinon : renvoie uniquement le contact solo (contact_id du payment_case)
 *
 * Utilisé par ConfirmPaymentDialog (TICKET-PAY-01, review-blocker-01) pour permettre
 * au staff d'identifier le payeur avant la confirmation manuelle virement/chèque.
 *
 * La logique est centralisée dans les RPC staff
 * `list_payment_case_contacts_for_staff` (00072) et
 * `list_crew_contacts_for_staff` (00241), avec contrôle d'accès explicite.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from './useSupabase';

export type PayerRole = 'pilot' | 'copilot' | null;

export interface PayerCandidate {
  contact_id: string;
  first_name: string | null;
  last_name: string | null;
  role: PayerRole;
}

interface UsePaymentCaseContactsParams {
  paymentCaseId: string | undefined;
  crewId?: string;
}

interface UsePaymentCaseContactsReturn {
  candidates: PayerCandidate[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface RpcRow {
  contact_id: string;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
}

function normalizeRole(role: string | null): PayerRole {
  if (role === 'pilot' || role === 'copilot') return role;
  return null;
}

export function usePaymentCaseContacts({
  paymentCaseId,
  crewId,
}: UsePaymentCaseContactsParams): UsePaymentCaseContactsReturn {
  const supabase = useSupabase();
  const [candidates, setCandidates] = useState<PayerCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCandidates = useCallback(async () => {
    if (!paymentCaseId && !crewId) {
      setCandidates([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = paymentCaseId
        ? await supabase.rpc('list_payment_case_contacts_for_staff', {
            p_case_id: paymentCaseId,
          })
        : await supabase.rpc('list_crew_contacts_for_staff', {
            p_crew_id: crewId,
          });

      if (fetchError) throw fetchError;

      const rows = ((data ?? []) as RpcRow[]).map((row) => ({
        contact_id: row.contact_id,
        first_name: row.first_name,
        last_name: row.last_name,
        role: normalizeRole(row.role),
      }));

      setCandidates(rows);
    } catch (err) {
      console.error('[usePaymentCaseContacts] Error:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des contacts');
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [crewId, paymentCaseId, supabase]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  return {
    candidates,
    loading,
    error,
    refresh: fetchCandidates,
  };
}
